import qs from 'qs'
import identity from 'lodash/identity'
import pickBy from 'lodash/pickBy'
import { libraryFilters } from '@edulastic/constants'
import { PA_SUBJECT_VALUES } from '@edulastic/constants/const/pear'
import { PDL_GRADES, PDL_SUBJECTS } from '@edulastic/constants/const/pdl'
import {
  getPDLDefaultGrade,
  getPDLGradeId,
  getPDLDefaultSubject,
  getPDLDefaultSubjectId,
} from '../../../AssessmentCreate/components/CreateAITest/ducks/helpers'

const TEST_LIST_LIMIT = 20

const CONTENT_ORCHARD_LIBRARY_PATH = '/home/content-orchard/public-library/all'

/** PA subject display name → label expected by Pear Content Orchard query (see PD public library filters). */
const PA_SUBJECT_TO_ORCHARD_SUBJECT = {
  [PA_SUBJECT_VALUES.MATHEMATICS]: 'Math',
  [PA_SUBJECT_VALUES.SCIENCE]: 'Science',
  [PA_SUBJECT_VALUES.ELA]: 'ELA',
  [PA_SUBJECT_VALUES.SOCIAL_STUDIES]: 'Social Studies',
  [PA_SUBJECT_VALUES.COMPUTER_SCIENCE]: 'Computer Science',
  [PA_SUBJECT_VALUES.OTHER]: 'Other Subjects',
}

/**
 * Map PDL grade code (from {@link getPDLDefaultGrade}) to Content Orchard `gradeLevels` tokens
 * (e.g. 1st, 2nd, K), using {@link PDL_GRADES} descriptions — same source as ILP / Pear Deck SDK.
 */
const pdlGradeCodeToContentOrchardGradeLevel = (pdlGradeCode) => {
  if (!pdlGradeCode) return null
  const row = PDL_GRADES.find(({ code }) => code === pdlGradeCode)
  if (!row?.description) return null
  if (row.description === 'Kindergarten') return 'K'
  return row.description.replace(/\s+Grade\s*$/i, '')
}

/**
 * Pear Assessment test library: Entire Library with grade, subject, standard set, and standard pre-filled.
 */
export const buildGroupAndAssignTestLibraryPath = ({
  curriculumId,
  grades = [],
  subjects = [],
  standardGroup,
}) => {
  const rawStandardId = standardGroup?._id

  const standardIds =
    rawStandardId != null && rawStandardId !== ''
      ? (() => {
          const numId = Number(rawStandardId)
          const _id = Number.isFinite(numId) ? numId : rawStandardId
          return [
            {
              _id,
              identifier: standardGroup?.identifier,
              pdlId: standardGroup?.pdlId,
            },
          ]
        })()
      : []

  const filterPayload = pickBy(
    {
      filter: libraryFilters.SMART_FILTERS.ENTIRE_LIBRARY,
      curriculumId: curriculumId || '',
      grades: grades.length ? grades : undefined,
      subject: subjects.length ? subjects : undefined,
      standardIds: standardIds.length ? standardIds : undefined,
      page: 1,
      limit: TEST_LIST_LIMIT,
    },
    identity
  )

  return `/author/tests?${qs.stringify(filterPayload)}`
}

/**
 * Pear Deck Content Orchard deep link on a given Pear host (from identity `redirect_url` origin).
 * Grade/subject selection matches {@link ILPDrawer} (`getPDLDefaultGrade`, `getPDLGradeId`, etc.).
 */
export const buildGroupAndAssignContentOrchardUrl = ({
  baseOrigin,
  grades = [],
  subjects = [],
  standardGroup,
}) => {
  if (!baseOrigin || typeof baseOrigin !== 'string') {
    return null
  }
  const origin = baseOrigin.replace(/\/$/, '')

  const params = new URLSearchParams()

  const pdlDefaultGrade = getPDLDefaultGrade(grades)
  const pdlDefaultGradeId = pdlDefaultGrade
    ? getPDLGradeId(pdlDefaultGrade)
    : null
  if (pdlDefaultGrade && pdlDefaultGradeId) {
    const level = pdlGradeCodeToContentOrchardGradeLevel(pdlDefaultGrade)
    if (level) params.append('gradeLevels', level)
  }

  const pdlDefaultSubject = getPDLDefaultSubject(subjects)
  const pdlDefaultSubjectId = pdlDefaultSubject
    ? getPDLDefaultSubjectId(pdlDefaultSubject)
    : null
  const firstPaSubject = subjects[0]
  const subjectLabel =
    (firstPaSubject && PA_SUBJECT_TO_ORCHARD_SUBJECT[firstPaSubject]) ||
    (pdlDefaultSubject &&
      PDL_SUBJECTS.find(({ code }) => code === pdlDefaultSubject)
        ?.description) ||
    null
  if (pdlDefaultSubject && pdlDefaultSubjectId && subjectLabel) {
    params.append('subjects', subjectLabel)
  }

  const pdlId = standardGroup?.pdlId
  if (pdlId != null && String(pdlId).trim() !== '') {
    params.append('pearStandardIds', String(pdlId).trim())
  }
  params.set('search', '')

  const query = params.toString()
  return `${origin}${CONTENT_ORCHARD_LIBRARY_PATH}?${query}`
}
